import { NgFor, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OwlDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import {
  NbButtonModule,
  NbCardModule,
  NbDateService,
  NbDialogRef,
  NbDialogService,
  NbIconModule,
  NbInputModule,
  NbOptionModule,
  NbSelectModule,
  NbToastrService,
  NbTooltipModule
} from '@nebular/theme';
import { Actions, Store, ofActionCompleted } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { EditInsightComponent } from 'src/app/features/insights/insights-table/edit-insight/edit-insight.component';
import { ReloadInsights } from 'src/app/features/insights/redux';
import { InsightsState } from 'src/app/features/insights/redux/insights.state';
import { InsightService } from 'src/app/features/insights/services/insight.service';
import { Campaign } from 'src/app/models/campaign';
import { FREQUENCIES } from 'src/app/models/frequencies';
import { Insight } from 'src/app/models/insight';
import { ACTIONS } from 'src/app/shared/constants/general-constants';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { RouterState } from '../../../../auth/redux';
import { ManageCampaign, PostSaveCampaignComplete } from '../../../redux';
import { CampaignService } from '../../../services/campaign.service';
/*
This component acts as a handler for activating and deactivating campagins
*/

@Component({
  selector: 'app-campaign-status-handler',
  templateUrl: './campaign-status-handler.component.html',
  styleUrls: ['./campaign-status-handler.component.css'],
  imports: [
    NbCardModule,
    NgIf,
    NbIconModule,
    NbButtonModule,
    NbInputModule,
    FormsModule,
    OwlDateTimeModule,
    NbSelectModule,
    NgFor,
    NbOptionModule,
    NbTooltipModule
  ]
})
export class CampaignStatusHandlerComponent implements OnInit, OnDestroy {
  public insight$: Observable<any[]> = inject(Store).select(InsightsState.allInsights);
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe = new Subject<void>();

  public insights = [];
  public action;
  public campaign: Campaign;
  public disableDatepicker = false;
  public FREQUENCIES = FREQUENCIES;
  public username = '';

  public startDateForDatepicker: Date;
  public endDateForDatepicker: Date;

  //Using to disable future dates in datepicker
  public minStartDate: Date;
  public minEndDate: Date;
  public requiredTimes = false;

  constructor(
    protected ref: NbDialogRef<CampaignStatusHandlerComponent>,
    private service: CampaignService,
    private insightService: InsightService,
    private action$: Actions,
    private toastrService: NbToastrService,
    private dialogService: NbDialogService,
    protected dateService: NbDateService<Date>
  ) {
    this.minStartDate = this.dateService.addMonth(this.dateService.today(), 0);
    this.minEndDate = this.dateService.addDay(this.dateService.today(), 1);
  }

  public ngOnInit() {
    this.insight$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.insights = data));
    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
    this.action$.pipe(ofActionCompleted(ReloadInsights), takeUntil(this.unsubscribe)).subscribe(() => {
      this.insight$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.insights = data));
    });
    this.action$.pipe(ofActionCompleted(PostSaveCampaignComplete), takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.ref.close();
      }
    });
    this.action$.pipe(ofActionCompleted(ManageCampaign), takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.ref.close();
      }
    });
    this.convertDateForPicker();
    if (this.action === ACTIONS.DEACTIVATE) {
      this.campaign.enabled = false;
      this.campaign.start_date = null;
      this.campaign.end_date = null;
      this.campaign.next_run_time = null;
      this.disableDatepicker = true;
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public clickedStart() {
    this.campaign.start_date = Math.round(this.startDateForDatepicker?.getTime() / 1000);
    this.campaign.end_date = Math.round(this.endDateForDatepicker?.getTime() / 1000);
    this.campaign.enabled = true;
    if (!this.campaign.start_date || !this.campaign.end_date) {
      this.toastrService.show(status, $localize`Start and End Times have to be provided to activate a Campaign.`, {
        status: 'danger',
        duration: 6000
      });

      return;
    }
    this.dialogService
      .open(NgxGenericModalComponent, {
        context: {
          title: $localize`Before activation you need to confirm you want to Activate": `,
          message: this.campaign.id
        }
      })
      .onClose.subscribe((data) => {
        if (data) {
          this.campaign.next_run_time = this.campaign.start_date;
          this.postCampaign(true);
        }
      });
  }

  public clickedStop() {
    this.dialogService
      .open(NgxGenericModalComponent, {
        context: {
          title: $localize`Before deactivation you need to confirm you want to Deactivate": `,
          message: this.campaign.id
        }
      })
      .onClose.subscribe((data) => {
        if (data) {
          this.postCampaign(false);
        }
      });
  }

  public close() {
    this.ref.close();
  }

  public convertDateForPicker() {
    if (this.campaign.start_date) {
      const date = new Date(0);
      date.setUTCSeconds(this.campaign.start_date);
      this.startDateForDatepicker = date;
    }
    if (this.campaign.end_date) {
      const date = new Date(0);
      date.setUTCSeconds(this.campaign.end_date);
      this.endDateForDatepicker = date;
    }

    if (this.campaign.start_date < this.campaign.end_date) {
      this.minEndDate = this.dateService.addDay(new Date(this.campaign.start_date * 1000), 1);
    }
  }

  public changeEvent() {
    if (this.startDateForDatepicker && this.endDateForDatepicker) {
      this.requiredTimes = true;
    }
    this.compareStartEndDate();
  }

  public compareStartEndDate() {
    if (this.endDateForDatepicker) {
      if (this.startDateForDatepicker.getTime() > this.endDateForDatepicker.getTime()) {
        this.endDateForDatepicker = this.dateService.addDay(new Date(this.startDateForDatepicker.getTime()), 1);
        this.minEndDate = this.dateService.addDay(new Date(this.startDateForDatepicker.getTime()), 1);
      } else {
        this.minEndDate = this.dateService.addDay(new Date(this.startDateForDatepicker.getTime()), 1);
      }
    } else {
      this.minEndDate = this.dateService.addDay(new Date(this.startDateForDatepicker.getTime()), 1);
    }
  }

  public postCampaign(activate) {
    let insightObject: Insight;
    if (this.insights.some(({ id }) => id === this.campaign.trigger_id)) {
      insightObject = Object.assign(
        {},
        this.insights.find(({ id }) => id === this.campaign.trigger_id)
      );
      if (this.action !== ACTIONS.DEACTIVATE) {
        if (insightObject.start_date && insightObject.start_date < this.campaign.start_date && insightObject.enabled) {
          this.campaign.last_updated_by = this.username;
          this.service.postCampaignsAmplify(this.campaign, null, activate);
        } else {
          this.dialogService
            .open(EditInsightComponent, {
              closeOnEsc: false,
              closeOnBackdropClick: false,
              context: {
                insight: insightObject
              }
            })
            .onClose.subscribe((data) => {
              if (data) {
                insightObject = Object.assign(
                  {},
                  this.insights.find(({ id }) => id === this.campaign.trigger_id)
                );
              }
            });
          this.toastrService.show(
            status,
            $localize`Insight Associated with the Campaign should be Activated and have Start Date before the Start Date of the Campaign.`,
            {
              status: 'danger',
              duration: 10000
            }
          );
        }
      } else {
        this.campaign.last_updated_by = this.username;
        this.service.postCampaignsAmplify(this.campaign, null, activate);
        insightObject.start_date = null;
        insightObject.end_date = null;
        insightObject.enabled = false;
        insightObject.next_run_time = null;
        this.insightService.postInsightsAmplify(insightObject, activate);
      }
    }
  }
}
